import { type EventCallback, EventEmitter } from '../utils/event-emitter';
import { subscribe, unsubscribe } from './api';
import { SSEEvent, SSEEventMap, SSEMarketBody, SubEvents } from './type';
import type { EventSourceClient, EventSourceMessage, FetchLikeInit, ReadyState } from 'eventsource-client';
import { createEventSource } from 'eventsource-client';
import ky, { Options } from 'ky';

// Use function overloads instead of conditional types
export type SSEApi = {
  readyState: ReadyState | undefined;
  lastEventId: string | undefined;
  connect: () => Promise<string>;
  disconnect: () => void;
  isConnected: () => boolean;
  eventNames: () => SSEEvent[];
  hasListeners: (eventName: SSEEvent) => boolean;
  addEventListener<T extends SubEvents>(
    eventName: T,
    callback: EventCallback<[data: SSEEventMap[T]]>,
    json: SSEMarketBody
  ): Promise<void>;
  addEventListener<T extends Exclude<SSEEvent, SubEvents>>(
    eventName: T,
    callback: EventCallback<[data: SSEEventMap[T]]>,
    json?: SSEMarketBody
  ): Promise<void>;
  removeEventListener<T extends SubEvents>(
    eventName: T,
    callback: EventCallback<[data: SSEEventMap[T]]>,
    json: SSEMarketBody
  ): Promise<void>;
  removeEventListener<T extends Exclude<SSEEvent, SubEvents>>(
    eventName: T,
    callback: EventCallback<[data: SSEEventMap[T]]>,
    json?: SSEMarketBody
  ): Promise<void>;
};

export const isSubEvent = (eventName: SSEEvent): eventName is SubEvents => {
  return ['market:new_trades', 'market:price_change', 'market:info_update'].includes(eventName);
};

export const getKey = (eventName: SSEEvent, json: SSEMarketBody) => {
  const { ...rest } = json;
  return `${eventName}:${rest.topics.join(',')}`;
};

export const createSSEApi = (initOptions: Options): SSEApi => {
  const mergedOptions: Options = { ...initOptions, prefixUrl: `${initOptions.prefixUrl}/sse` };
  const sseApi = ky.extend(mergedOptions);
  const emitter = new EventEmitter<{ [K in SSEEvent]: [SSEEventMap[K]] }>();
  // Per (callback, eventName) wrapper store. The same callback is registered for
  // multiple events (price_change + info_update + base:connected), so keying by
  // callback alone overwrites earlier wrappers and leaks emitter listeners on
  // unmount. Nesting by eventName lets removeEventListener detach the right one.
  const callbackMap = new WeakMap<EventCallback<any>, Map<SSEEvent, EventCallback<any>>>();
  // Refcounted server subscriptions keyed by `event:topics`. count tracks how
  // many mounted consumers share the subscription so the first unmount doesn't
  // tear down a topic another consumer is still using.
  const pendingOps = new Map<string, { json: SSEMarketBody; count: number }>();
  let sse: EventSourceClient | undefined;
  let connectionId: string | undefined;
  let connectPromise: Promise<string> | null = null;
  // Reconnection state
  let isManualDisconnect = false;
  let reconnectAttempts = 0;
  let reconnectTimer: NodeJS.Timeout | undefined;
  const maxReconnectDelay = 30000;

  const isConnected = () => !!connectionId;
  const resetConnection = () => {
    connectionId = undefined;
    connectPromise = null;
  };
  const disconnect = () => {
    isManualDisconnect = true;
    clearTimeout(reconnectTimer);
    sse?.close();
    sse = undefined;
    resetConnection();
    pendingOps.clear();
  };

  const resubscribe = async () => {
    if (!connectionId) return;
    for (const [key, { json }] of pendingOps) {
      try {
        json.connection_id = connectionId;
        await subscribe(sseApi, json);
      } catch (error) {
        console.error('[SSE] Resubscribe failed for key:', key, error);
      }
    }
  };

  const scheduleReconnect = () => {
    if (isManualDisconnect) return;
    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), maxReconnectDelay);
    console.log(`[SSE] Reconnecting in ${delay}ms... (Attempt ${reconnectAttempts + 1})`);
    reconnectTimer = setTimeout(() => {
      reconnectAttempts++;
      connect().catch(err => {
        console.error('[SSE] Reconnect failed:', err);
        scheduleReconnect();
      });
    }, delay);
  };

  const connect = async (): Promise<string> => {
    if (!connectPromise && !sse) {
      isManualDisconnect = false;
    }
    if (connectionId) return connectionId;
    if (connectPromise) return connectPromise;
    if (!sse) {
      sse = createEventSource({
        url: './connect',
        method: 'POST',
        onMessage(message: EventSourceMessage) {
          const { event, data } = message as EventSourceMessage & { event: SSEEvent };
          const result = JSON.parse(data) as SSEEventMap[SSEEvent];
          if (event === 'base:connected') {
            connectionId = (result as SSEEventMap['base:connected']).connection_id;
          }
          emitter.emit(event, result);
        },
        onDisconnect() {
          if (connectionId) {
            emitter.emit('base:disconnected', { connection_id: connectionId });
          }
          sse?.close();
          sse = undefined;
          resetConnection();
          if (!isManualDisconnect) {
            scheduleReconnect();
          }
        },
        fetch: async (url: string | URL, init?: FetchLikeInit) => {
          const options = { ...mergedOptions, ...init };
          return await sseApi(url, options);
        },
      });
    }

    connectPromise = new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        sse?.close();
        sse = undefined;
        resetConnection();
        reject(new Error('Connection timeout'));
      }, mergedOptions.timeout || 10e3);
      const handleConnected = (data: SSEEventMap['base:connected']) => {
        clearTimeout(timeout);
        connectionId = data.connection_id;
        emitter.off('base:connected', handleConnected);
        connectPromise = null;
        reconnectAttempts = 0;
        resubscribe();
        resolve(connectionId);
      };
      emitter.once('base:connected', handleConnected);
      sse?.connect();
    });
    return connectPromise;
  };

  const ensureConnection = async (json?: SSEMarketBody): Promise<string> => {
    const id = await connect();
    if (json && !json.connection_id) json.connection_id = id;
    return id;
  };

  return {
    connect,
    disconnect,
    readyState: sse?.readyState,
    lastEventId: sse?.lastEventId,
    isConnected,
    eventNames: () => emitter.eventNames(),
    hasListeners: (eventName: SSEEvent) => emitter.hasListeners(eventName),
    addEventListener: async (
      eventName: SSEEvent,
      callback: EventCallback<SSEEventMap[SSEEvent][]>,
      json?: SSEMarketBody
    ) => {
      const wrappedCallback = (data: SSEEventMap[SSEEvent]) => callback(data);
      emitter.on(eventName, wrappedCallback);
      let perEvent = callbackMap.get(callback);
      if (!perEvent) {
        perEvent = new Map();
        callbackMap.set(callback, perEvent);
      }
      perEvent.set(eventName, wrappedCallback);
      if (isSubEvent(eventName) && json) {
        await ensureConnection(json);
        const key = getKey(eventName, json);
        const entry = pendingOps.get(key);
        if (entry) {
          entry.count++;
        } else {
          pendingOps.set(key, { json, count: 1 });
          await subscribe(sseApi, json);
        }
      }
    },
    removeEventListener: async (
      eventName: SSEEvent,
      callback: EventCallback<SSEEventMap[SSEEvent][]>,
      json?: SSEMarketBody
    ) => {
      const perEvent = callbackMap.get(callback);
      const wrappedCallback = perEvent?.get(eventName);
      if (perEvent && wrappedCallback) {
        emitter.off(eventName, wrappedCallback);
        perEvent.delete(eventName);
        if (perEvent.size === 0) callbackMap.delete(callback);
      }
      if (isSubEvent(eventName) && json) {
        const key = getKey(eventName, json);
        const entry = pendingOps.get(key);
        if (entry) {
          entry.count--;
          if (entry.count <= 0) {
            pendingOps.delete(key);
            if (isConnected()) {
              await unsubscribe(sseApi, { ...json, connection_id: connectionId });
            }
          }
        }
      }
    },
  };
};

export default createSSEApi;
